// src/personal/edit/EditPersonalForm.jsx
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import api from '../../../config/axiosInstance';
import { useSnackbar } from 'notistack';
import EditFormFields from './EditFormFields';
import DialogEditComponent from './dialog/DialogEditComponent';
import DeactivateDialog from './dialog/DeactivateDialog';
import CalendarSyncProgressOverlay from '../dialogs/CalendarSyncProgressOverlay';
import { suggestRestPolicyFromWorkarea } from '../planningPolicyOptions';
import { UserContext } from '../../context/UserContext';
import {
  hasAnyRequiredPermission,
  PERSONAL_ASSIGNMENT_EDIT_PERMISSIONS,
  PERSONAL_DATA_EDIT_PERMISSIONS,
} from '../../../config/routePermissions';
import {
  createEmptyWeeklySchedule,
  isWeeklyScheduleComplete,
  isWeeklyScheduleEmpty,
  normalizeWeeklySchedule,
  serializeWeeklySchedule,
  CYCLE_WEEK_ALL,
} from '../weeklyScheduleUtils';

const SPANISH_MONTHS = {
  enero: '01',
  febrero: '02',
  marzo: '03',
  abril: '04',
  mayo: '05',
  junio: '06',
  julio: '07',
  agosto: '08',
  septiembre: '09',
  setiembre: '09',
  octubre: '10',
  noviembre: '11',
  diciembre: '12',
};

export const toIsoDate = (raw) => {
  if (!raw) return '';

  const value = String(raw).trim();
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const spanishMatch = value
    .toLocaleLowerCase('es')
    .match(/(?:[a-záéíóúñ]+,\s*)?(\d{1,2})\s+de\s+([a-záéíóúñ]+)\s+de\s+(\d{4})/i);
  if (spanishMatch) {
    const [, day, monthName, year] = spanishMatch;
    const month = SPANISH_MONTHS[monthName];
    if (month) return `${year}-${month}-${day.padStart(2, '0')}`;
  }

  const date = new Date(value);
  return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
};

const EditPersonalForm = ({ 
  employeeData, 
  primaryWorkareaId, 
  auxFixedIds = [], 
  auxValidFrom,
  isFormDirty, 
  setIsFormDirty,
  isEmployeeInactive,
  onActivateEmployee,
  activating,
  onRefresh,  // 👈 NUEVA PROP
  licenses,
  history,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { permissions } = useContext(UserContext);
  const dataForm = employeeData?.data_form?.[0] || {};
  const backendPermissionFlags = employeeData?.permission_flags || {};
  const canEditPersonalData =
    backendPermissionFlags.can_edit_data ??
    hasAnyRequiredPermission(permissions, PERSONAL_DATA_EDIT_PERMISSIONS);
  const canEditAssignments =
    backendPermissionFlags.can_edit_assignments ??
    hasAnyRequiredPermission(permissions, PERSONAL_ASSIGNMENT_EDIT_PERMISSIONS);

  const todayIso = () => new Date().toISOString().split('T')[0];

  // Estado base (respeta lo que ya tenías)
  const [formValues, setFormValues] = useState({
    ci:      dataForm.ci?.value      || '',
    name:    dataForm.name?.value    || '',
    surname: dataForm.surname?.value || '',
    area_position:   String(dataForm.area_position?.id   || ''),       // se mantiene por compatibilidad visual
    override_manager: dataForm.override_manager?.value === 'SI' ? '1' : '0',
    first_day:       toIsoDate(dataForm.first_day?.value),
    enabled:         String(dataForm.enabled?.value       || ''),
    station:         String(dataForm.station?.id         || ''),
    is_manager:      dataForm.is_manager?.value === 'SI' ? '1' : '0',
    is_night:        dataForm.is_night?.value === 'SI' ? '1' : '0',
    override_night:  dataForm.override_night?.value === 'SI' ? '1' : '0',
    employment_regime: 'monthly',
    rest_policy:       dataForm.rest_policy?.value || 'standard_6',
    assignment_mode:   dataForm.assignment_mode?.value || 'regular',
    workarea:        String(dataForm.rol_position?.id     || ''),      // <- id rol actual
    aux_valid_from:   toIsoDate(auxValidFrom) || todayIso(),
  });
  const [originalValues, setOriginalValues] = useState(formValues);

  const [confirmOpen,    setConfirmOpen]    = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [weeklyScheduleSupported, setWeeklyScheduleSupported] = useState(Boolean(employeeData?.weekly_schedule_supported));
  const [weeklySchedule, setWeeklySchedule] = useState(
    normalizeWeeklySchedule(employeeData?.weekly_schedule || createEmptyWeeklySchedule())
  );
  const [originalWeeklySchedule, setOriginalWeeklySchedule] = useState(
    normalizeWeeklySchedule(employeeData?.weekly_schedule || createEmptyWeeklySchedule())
  );
  const [weeklyScheduleError, setWeeklyScheduleError] = useState('');
  const [stationWorkshifts, setStationWorkshifts] = useState(
    employeeData?.workshifts_by_station?.[String(dataForm.station?.id || '')] || []
  );
  const [rotationWorkshiftSupported, setRotationWorkshiftSupported] = useState(
    Boolean(employeeData?.rotation_workshift_supported)
  );
  const [rotationWorkshifts, setRotationWorkshifts] = useState([]); // array de { id, name }
  const [originalRotationWorkshiftIds, setOriginalRotationWorkshiftIds] = useState(
    (employeeData?.rotation_workshift_ids || []).map(Number).sort()
  );

  const fieldLabels = {
    ci:              dataForm.ci?.name              || 'Cédula',
    name:            dataForm.name?.name            || 'Nombres',
    surname:         dataForm.surname?.name         || 'Apellidos',
    area_position:   dataForm.area_position?.name   || 'Rol',
    override_manager:dataForm.override_manager?.name|| 'Puede cubrir al Encargado',
    first_day:       dataForm.first_day?.name       || 'Fecha de ingreso',
    enabled:         dataForm.enabled?.name         || 'Estado',
    station:         dataForm.station?.name         || 'Estación',
    is_manager:      dataForm.is_manager?.name      || 'Es encargado',
    is_night:        dataForm.is_night?.name        || 'Puede hacer nocturnidad',
    override_night:  dataForm.override_night?.name  || 'Puede cubrir turno nocturno',
    employment_regime: dataForm.employment_regime?.name || 'Régimen laboral',
    rest_policy:       dataForm.rest_policy?.name || 'Política de descansos',
    assignment_mode:   dataForm.assignment_mode?.name || 'Modalidad de asignación',
    workarea:        dataForm.rol_position?.name    || 'Área de trabajo',
    aux_valid_from:  'Vigencia roles auxiliares',
  };

  // ===== NUEVO: opciones combinadas "Área – Rol" + selección principal/auxiliares =====
  // Deps estables para evitar react-hooks/exhaustive-deps
  const rawPositions = dataForm.possible_position;
  const rawWorkareas = dataForm.possible_workarea;

  // Memo con fallback dentro del callback (no en el array de deps)
  const positions = useMemo(() => rawPositions ?? [], [rawPositions]);
  const workareas = useMemo(() => rawWorkareas ?? [], [rawWorkareas]);

  const positionNameById = useMemo(() => {
    const m = new Map();
    positions.forEach(p => m.set(p.id, p.name));
    return m;
  }, [positions]);

  const comboOptions = useMemo(() => {
    const seen = new Set();
    return (workareas || [])
      .filter(wa => wa && wa.id != null)
      .map(wa => {
        const positionId   = wa.position;
        const positionName = positionNameById.get(positionId) || 'Área';
        const roleName     = wa.name || 'Posición';
        return {
          id: wa.id,
          positionId,
          positionName,
          roleName,
          label: `${positionName} – ${roleName}`,
        };
      })
      .filter(opt => (seen.has(opt.id) ? false : (seen.add(opt.id), true)))
      .sort((a, b) => (a.positionName + a.roleName).localeCompare(b.positionName + b.roleName));
  }, [workareas, positionNameById]);

  // Selecciones locales
  // primaryWAs: array de objetos de comboOptions, todos de la MISMA área -- el
  // primero es el que se guarda como id_workarea principal, el resto (si hace
  // más de una posición dentro de la misma área, ej. Caja + Reponedor) se
  // manda igual que un auxiliar. auxWAs queda solo para auxiliares CRUZADOS
  // a otra área (la flexibilidad real: alguien de Pista que también cubre Mini).
  const [primaryWAs, setPrimaryWAs] = useState([]);
  const [auxWAs, setAuxWAs]         = useState([]);

  // Cargar selección inicial desde backend (rol actual + auxiliares fijos)
  useEffect(() => {
    // Rol principal: preferimos `primaryWorkareaId` si viene, si no, el `formValues.workarea`
    const primaryId = primaryWorkareaId ?? (formValues.workarea ? Number(formValues.workarea) : null);
    const primaryOpt = comboOptions.find(o => o.id === primaryId) || null;
    const primaryAreaId = primaryOpt?.positionId ?? null;

    const auxOptAll = (auxFixedIds || [])
      .map(id => comboOptions.find(o => o.id === id))
      .filter(Boolean)
      .filter(o => !primaryOpt || o.id !== primaryOpt.id);

    // Auxiliares ya cargados que caen en la MISMA área que el principal pasan
    // a ser chips adicionales de posición; el resto (otra área) queda como
    // auxiliar de verdad.
    const samePositionAux = auxOptAll.filter(o => o.positionId === primaryAreaId);
    const crossPositionAux = auxOptAll.filter(o => o.positionId !== primaryAreaId);

    setPrimaryWAs(primaryOpt ? [primaryOpt, ...samePositionAux] : []);
    setAuxWAs(crossPositionAux);
  }, [comboOptions, primaryWorkareaId, auxFixedIds, formValues.workarea]);

  // Turnos permitidos para "Rotación restringida" -- se resuelven contra los
  // turnos habilitados de la estación (mismo listado que el horario fijo).
  // Depende solo de rotation_workshift_ids (no de stationWorkshifts) para que
  // un cambio de estación en el formulario no pise una selección en curso;
  // eso se poda aparte, más abajo.
  useEffect(() => {
    const ids = (employeeData?.rotation_workshift_ids || []).map(Number);
    const resolved = ids
      .map((id) => (stationWorkshifts || []).find((ws) => Number(ws.id) === id))
      .filter(Boolean);
    setRotationWorkshifts(resolved);
    setOriginalRotationWorkshiftIds(ids.slice().sort());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeData?.rotation_workshift_ids]);

  // Si cambia la estación seleccionada en el formulario, los turnos elegidos
  // que ya no pertenezcan a la nueva estación se quitan (no se resetea todo).
  useEffect(() => {
    setRotationWorkshifts((prev) =>
      prev.filter((ws) => (stationWorkshifts || []).some((opt) => Number(opt.id) === Number(ws.id)))
    );
  }, [stationWorkshifts]);

  // Cuando cambian datos del backend, reseteamos el "dirty"
  useEffect(() => {
    const initial = {
      ci:      dataForm.ci?.value      || '',
      name:    dataForm.name?.value    || '',
      surname: dataForm.surname?.value || '',
      area_position:   String(dataForm.area_position?.id   || ''),
      override_manager: dataForm.override_manager?.value === 'SI' ? '1' : '0',
      first_day:       toIsoDate(dataForm.first_day?.value),
      enabled:         String(dataForm.enabled?.value       || ''),
      station:         String(dataForm.station?.id         || ''),
      is_manager:      dataForm.is_manager?.value === 'SI' ? '1' : '0',
      is_night:        dataForm.is_night?.value === 'SI' ? '1' : '0',
      override_night:  dataForm.override_night?.value === 'SI' ? '1' : '0',
      employment_regime: 'monthly',
      rest_policy:       dataForm.rest_policy?.value || 'standard_6',
      assignment_mode:   dataForm.assignment_mode?.value || 'regular',
      workarea:        String(dataForm.rol_position?.id     || ''),
      aux_valid_from:   toIsoDate(auxValidFrom) || todayIso(),
    };
    setFormValues(initial);
    setOriginalValues(initial);
    const normalizedWeeklySchedule = normalizeWeeklySchedule(
      employeeData?.weekly_schedule || createEmptyWeeklySchedule()
    );
    setWeeklyScheduleSupported(Boolean(employeeData?.weekly_schedule_supported));
    setWeeklySchedule(normalizedWeeklySchedule);
    setOriginalWeeklySchedule(normalizedWeeklySchedule);
    setWeeklyScheduleError('');
    setRotationWorkshiftSupported(Boolean(employeeData?.rotation_workshift_supported));
    setStationWorkshifts(
      employeeData?.workshifts_by_station?.[String(dataForm.station?.id || '')] || []
    );
    setIsFormDirty(false);
  }, [
    dataForm.ci?.value,
    dataForm.name?.value,
    dataForm.surname?.value,
    dataForm.area_position?.id,
    dataForm.override_manager?.value,
    dataForm.first_day?.value,
    dataForm.enabled?.value,
    dataForm.station?.id,
    dataForm.is_manager?.value,
    dataForm.is_night?.value,
    dataForm.override_night?.value,
    dataForm.rest_policy?.value,
    dataForm.assignment_mode?.value,
    dataForm.rol_position?.id,
    auxValidFrom,
    employeeData?.weekly_schedule,
    employeeData?.weekly_schedule_supported,
    employeeData?.rotation_workshift_supported,
    employeeData?.workshifts_by_station,
    setIsFormDirty,
  ]);

  useEffect(() => {
    if (!formValues.station) {
      setStationWorkshifts([]);
      return;
    }

    setStationWorkshifts(
      employeeData?.workshifts_by_station?.[String(formValues.station)] || []
    );
  }, [employeeData?.workshifts_by_station, formValues.station]);

  // "Sucio" se recalcula de forma reactiva a partir del estado actual, no de
  // forma imperativa en cada setter. Antes cada setter (primario, auxiliares,
  // horario fijo) llamaba a markDirty por su cuenta con una copia cerrada del
  // resto del estado -- si dos setters se disparaban en el mismo evento (ej.
  // agregar un chip de posición principal también dispara un ajuste de
  // auxiliares), el segundo podía pisar el resultado del primero con datos
  // desactualizados y el botón Guardar quedaba deshabilitado aunque sí había
  // cambios. Un solo efecto que mira el estado posterior a cada render evita
  // esa carrera.
  useEffect(() => {
    const baseDirty = JSON.stringify(formValues) !== JSON.stringify(originalValues);
    const weeklyDirty =
      JSON.stringify(normalizeWeeklySchedule(weeklySchedule)) !==
      JSON.stringify(normalizeWeeklySchedule(originalWeeklySchedule));

    const nextPrimaryId = primaryWAs?.[0]?.id ?? null;
    const originalPrimaryId = primaryWorkareaId ?? Number(originalValues.workarea || null);
    // "Auxiliar" combinado para comparar contra lo que vino del backend:
    // chips extra de la misma área (además del principal) + auxiliares cruzados.
    const nextCombinedAuxIds = [
      ...(primaryWAs || []).slice(1).map(o => o.id),
      ...(auxWAs || []).map(o => o.id),
    ].sort();
    const originalAuxIds = (auxFixedIds || []).slice().sort();

    const comboDirty =
      (nextPrimaryId || null) !== (originalPrimaryId || null) ||
      JSON.stringify(nextCombinedAuxIds) !== JSON.stringify(originalAuxIds);

    const nextRotationIds = (rotationWorkshifts || []).map((ws) => Number(ws.id)).sort();
    const rotationDirty = JSON.stringify(nextRotationIds) !== JSON.stringify(originalRotationWorkshiftIds);

    setIsFormDirty(baseDirty || comboDirty || weeklyDirty || rotationDirty);
  }, [
    formValues,
    originalValues,
    weeklySchedule,
    originalWeeklySchedule,
    primaryWAs,
    auxWAs,
    primaryWorkareaId,
    auxFixedIds,
    rotationWorkshifts,
    originalRotationWorkshiftIds,
    setIsFormDirty,
  ]);

  const handleChange = ({ target: { name, value, type, checked }}) => {
    const nextValue = type === 'checkbox' ? (checked ? '1' : '0') : value;
    setFormValues((prev) => {
      const next = { ...prev, [name]: nextValue };
      // Al marcar "Es encargado" la sección de días fijos queda visible y editable,
      // así que la modalidad debe reflejar eso para no quedar incoherente.
      if (name === 'is_manager' && nextValue === '1' && prev.assignment_mode === 'regular') {
        next.assignment_mode = 'fixed_schedule';
      }
      return next;
    });

    if (name === 'is_manager' && nextValue === '1') {
      // Precarga visual del default que también siembra el backend al guardar
      // un encargado sin días fijos configurados todavía -- editable antes de guardar.
      setWeeklySchedule((prev) =>
        isWeeklyScheduleEmpty(prev)
          ? [{ weekday: 6, cycle_week: CYCLE_WEEK_ALL, workshift_id: '', is_free_day: true }]
          : prev
      );
    }
  };

  const handleSave = () => { if (isFormDirty) setConfirmOpen(true); };

  const handleConfirmSave = async () => {
    const primaryWA = primaryWAs?.[0] || null;
    if (!primaryWA) {
      enqueueSnackbar('Seleccioná el Área y la Posición principal', { variant: 'warning' });
      setConfirmOpen(false);
      return;
    }

    if (formValues.assignment_mode === 'fixed_schedule') {
      if (!weeklyScheduleSupported) {
        enqueueSnackbar('Esta base todavía no tiene habilitados los horarios fijos semanales.', { variant: 'warning' });
        setConfirmOpen(false);
        return;
      }
      if (isWeeklyScheduleEmpty(weeklySchedule)) {
        setWeeklyScheduleError('Marcá al menos un día con turno fijo o libre.');
        enqueueSnackbar('Marcá al menos un día con turno fijo o libre.', { variant: 'warning' });
        setConfirmOpen(false);
        return;
      }
      if (!isWeeklyScheduleComplete(weeklySchedule)) {
        setWeeklyScheduleError('Completá el turno o "Libre" para cada día marcado.');
        enqueueSnackbar('Completá el turno o "Libre" para cada día marcado.', { variant: 'warning' });
        setConfirmOpen(false);
        return;
      }
    }

    if (formValues.assignment_mode === 'restricted_rotation') {
      if (!rotationWorkshiftSupported) {
        enqueueSnackbar('Esta base todavía no tiene habilitada la rotación restringida.', { variant: 'warning' });
        setConfirmOpen(false);
        return;
      }
      if (!rotationWorkshifts.length) {
        enqueueSnackbar('Elegí al menos un turno permitido para rotar.', { variant: 'warning' });
        setConfirmOpen(false);
        return;
      }
    }

    // Auxiliares a mandar: chips extra de posición dentro de la misma área
    // (además del principal) + auxiliares cruzados de la otra área.
    const extraPrimaryIds = (primaryWAs || []).slice(1).map(o => o.id);
    const auxIds = [...extraPrimaryIds, ...(auxWAs || []).map(o => o.id)].filter(id => id !== primaryWA.id);
    const params = new URLSearchParams({
      ci: formValues.ci,
      name: formValues.name,
      surname: formValues.surname,
      // NOTA: el backend actual no requiere "position", con id_workarea alcanza
      override_manager: formValues.override_manager,
      is_night: formValues.is_night,
      override_night: formValues.override_night,
      employment_regime: 'monthly',
      rest_policy: formValues.rest_policy,
      assignment_mode: formValues.assignment_mode,
      first_day: formValues.first_day,
      aux_valid_from: formValues.aux_valid_from,
      enabled: formValues.enabled,
      station: formValues.station,
      is_manager: formValues.is_manager,
      workarea: String(primaryWA.id),
      aux_workareas: auxIds.join(','), // CSV (compat con backend)
      weekly_schedule:
        formValues.assignment_mode === 'fixed_schedule'
          ? JSON.stringify(serializeWeeklySchedule(weeklySchedule))
          : '',
      rotation_workshift_ids:
        formValues.assignment_mode === 'restricted_rotation'
          ? JSON.stringify(rotationWorkshifts.map((ws) => Number(ws.id)))
          : '',
    });

    try {
      setSaving(true);
      const res = await api.put(`personal/edit_save?${params.toString()}`);
      if (res.status === 200) {
        const updatedCalendars = Number(res?.data?.calendar_sync?.updated_calendars || 0);
        const detail = updatedCalendars > 0
          ? ` Se actualizaron ${updatedCalendars} calendario(s) abierto(s).`
          : '';
        enqueueSnackbar(`Cambios guardados correctamente.${detail}`, { variant: 'success' });
        setConfirmOpen(false);
        // actualizamos "original" con lo que quedó
        setOriginalValues({
          ...formValues,
          employment_regime: 'monthly',
          rest_policy: formValues.rest_policy,
          assignment_mode: formValues.assignment_mode,
          workarea: String(primaryWA.id),
          aux_valid_from: formValues.aux_valid_from,
        });
        setOriginalWeeklySchedule(normalizeWeeklySchedule(weeklySchedule));
        setWeeklyScheduleError('');
        setOriginalRotationWorkshiftIds((rotationWorkshifts || []).map((ws) => Number(ws.id)).sort());
        setIsFormDirty(false);
        if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (err) {
      console.error('Error guardando:', err);
      enqueueSnackbar(err.response?.data?.error || err.response?.data?.message || 'No se pudieron guardar los cambios', {
        variant: 'error',
      });
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = () => setDeactivateOpen(true);
  
  const handleConfirmDeactivate = async ({ deactivationDate, scheduled }) => {
    setDeactivating(true);
    try {
      const params = new URLSearchParams({
        ci: formValues.ci,
        deactivation_date: deactivationDate,
        scheduled: String(scheduled),
      });
      const response = await api.put(`personal/edit_delete?${params.toString()}`);
      const updatedCalendars = Number(response?.data?.calendar_sync?.updated_calendars || 0);
      const detail = updatedCalendars > 0
        ? ` Se actualizaron ${updatedCalendars} calendario(s) abierto(s).`
        : '';
      enqueueSnackbar(`Baja registrada correctamente.${detail}`, { variant: 'success' });
      setDeactivateOpen(false);
      // 👇 RECARGAR DATOS DESPUÉS DE DESACTIVAR
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error('Error desactivando:', err);
      enqueueSnackbar(err.response?.data?.error || 'No se pudo registrar la baja', { variant: 'error' });
      setDeactivateOpen(false);
    } finally {
      setDeactivating(false);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setDeactivateOpen(false);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {employeeData?.name}
      </Typography>

      <EditFormFields
        formValues={formValues}
        fieldLabels={fieldLabels}
        handleChange={handleChange}
        isFormDirty={isFormDirty}
        handleSave={handleSave}
        handleDeactivate={handleDeactivate}
        employeeData={employeeData}
        // NUEVO: props para los combos
        comboOptions={comboOptions}
        primaryWAs={primaryWAs}
        setPrimaryWAs={(value) => {
          const nextPrimaryWAs = Array.isArray(value) ? value : [];
          setPrimaryWAs(nextPrimaryWAs);
          const suggestedRestPolicy = suggestRestPolicyFromWorkarea(nextPrimaryWAs[0] || null);
          setFormValues((prev) => ({ ...prev, rest_policy: suggestedRestPolicy }));
        }}
        auxWAs={auxWAs}
        setAuxWAs={(value) => {
          setAuxWAs(Array.isArray(value) ? value : []);
        }}
        weeklyScheduleSupported={weeklyScheduleSupported}
        weeklySchedule={weeklySchedule}
        setWeeklySchedule={(nextRows) => {
          setWeeklySchedule(normalizeWeeklySchedule(nextRows));
          setWeeklyScheduleError('');
        }}
        weeklyScheduleError={weeklyScheduleError}
        stationWorkshifts={stationWorkshifts}
        rotationWorkshiftSupported={rotationWorkshiftSupported}
        rotationWorkshifts={rotationWorkshifts}
        setRotationWorkshifts={(value) => {
          setRotationWorkshifts(Array.isArray(value) ? value : []);
        }}
        canEditPersonalData={canEditPersonalData}
        canEditAssignments={canEditAssignments}
        // NUEVO: props para activar/desactivar
        isEmployeeInactive={isEmployeeInactive}
        onActivateEmployee={onActivateEmployee}
        activating={activating}
        saving={saving}
        // Inasistencias / Historial (pestañas)
        licenses={licenses}
        history={history}
        employmentPeriods={employeeData?.employment_periods || []}
        onRefresh={onRefresh}
      />

      <DialogEditComponent
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirmSave}
        formValues={formValues}
        originalValues={originalValues}
        fieldLabels={fieldLabels}
        showCalendarImpactNotice
      />

      <DeactivateDialog
        open={deactivateOpen}
        onClose={handleCancel}
        onConfirm={handleConfirmDeactivate}
        ci={formValues.ci}
      />

      <CalendarSyncProgressOverlay open={saving} variant="update" />
      <CalendarSyncProgressOverlay open={deactivating} variant="deactivate" />
    </Box>
  );
};

export default EditPersonalForm;
